import React, { useState } from "react";
import { Pencil, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { formatError } from "@/lib/auth";

/** Delete button with a confirm dialog. */
export function ConfirmDelete({ title, description, onConfirm, testId, label = "Delete" }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-rose-300 hover:text-rose-200" data-testid={testId}>
          <Trash2 className="w-3.5 h-3.5 mr-1" /> {label}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testId}-cancel`}>Cancel</AlertDialogCancel>
          <AlertDialogAction className="bg-rose-700 hover:bg-rose-600" onClick={onConfirm} data-testid={`${testId}-confirm`}>{label}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Inline text editor: shows the value with a pencil, edits in place. */
export function InlineEdit({ value, onSave, testId, className = "", inputClass = "w-48" }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value);
  const save = async () => {
    try {
      await onSave(text.trim());
      setEditing(false);
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  if (!editing) {
    return (
      <span className={`inline-flex items-center gap-1 ${className}`}>
        {value}
        <button type="button" onClick={() => { setText(value); setEditing(true); }} className="text-muted-foreground hover:text-foreground" data-testid={`${testId}-edit`}>
          <Pencil className="w-3 h-3" />
        </button>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} className={`h-7 text-xs ${inputClass}`} autoFocus data-testid={`${testId}-input`} />
      <button type="button" onClick={save} disabled={!text.trim()} className="text-emerald-300" data-testid={`${testId}-save`}><Check className="w-3.5 h-3.5" /></button>
      <button type="button" onClick={() => setEditing(false)} className="text-muted-foreground" data-testid={`${testId}-cancel`}><X className="w-3.5 h-3.5" /></button>
    </span>
  );
}
